import React, { useState, useEffect } from 'react'
import { 
  UserIcon,
  IdentificationIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { userApi } from '../../services/api'
import { validateFullName, validatePhone, validatePincode } from '../../utils/validation'

interface UserProfile {
  id: number
  email: string
  username: string
  full_name: string
  phone?: string
  is_active: boolean
  is_verified: boolean
  is_admin: boolean
  pan_number?: string
  kyc_status: string
  created_at: string
  last_login?: string
  date_of_birth?: string
  address?: string
  city?: string
  state?: string
  pincode?: string
  referral_code?: string
  kyc?: {
    id: number
    full_name: string
    date_of_birth: string
    gender: string
    father_name: string
    address_line1: string
    address_line2?: string
    city: string
    state: string
    pincode: string
    country: string
    pan_number: string
    aadhar_number?: string
    bank_name?: string
    account_number?: string
    ifsc_code?: string
    annual_income?: string
    occupation?: string
    investment_experience?: string
    risk_tolerance?: string
    status: string
    esign_status: string
    admin_remarks?: string
    submitted_at: string
    reviewed_at?: string
    approved_at?: string
  }
}

const ProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    date_of_birth: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      const profileData = await userApi.getProfile()
      setProfile(profileData)
      
      // Initialize form data
      setFormData({
        full_name: profileData.full_name || '',
        phone: profileData.phone || '',
        date_of_birth: profileData.date_of_birth ? profileData.date_of_birth.split('T')[0] : '',
        address: profileData.address || '',
        city: profileData.city || '',
        state: profileData.state || '',
        pincode: profileData.pincode || ''
      })
    } catch (error) {
      console.error('Error fetching profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const validateProfileField = (field: 'full_name' | 'phone' | 'pincode', value: string) => {
    if (field === 'full_name') {
      if (value.trim() && !validateFullName(value)) return 'Please enter a valid full name.'
      return ''
    }
    if (field === 'pincode') {
      if (value.trim() && !validatePincode(value)) return 'Please enter a valid 6-digit PIN code.'
      return ''
    }
    if (!value.trim()) return 'Please enter a valid 10-digit mobile number.'
    if (!validatePhone(value)) return 'Please enter a valid 10-digit mobile number.'
    return ''
  }

  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setFormErrors(prev => {
      const next = { ...prev }
      if (field === 'full_name' || field === 'phone' || field === 'pincode') {
        const fieldError = validateProfileField(field, value)
        if (fieldError) next[field] = fieldError
        else delete next[field]
      }
      return next
    })
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors: Record<string, string> = {}
    const fullNameError = validateProfileField('full_name', formData.full_name)
    const phoneError = validateProfileField('phone', formData.phone)
    const pincodeError = validateProfileField('pincode', formData.pincode)
    if (fullNameError) errors.full_name = fullNameError
    if (phoneError) errors.phone = phoneError
    if (pincodeError) errors.pincode = pincodeError
    setFormErrors(errors)
    if (Object.keys(errors).length > 0) return
    try {
      const updateData = {
        ...formData,
        date_of_birth: formData.date_of_birth || undefined
      }
      
      await userApi.updateProfile(updateData)
      setEditMode(false)
      fetchProfile()
    } catch (error) {
      console.error('Error updating profile:', error)
    }
  }

  const getKycDisplayStatus = () => {
    if (!profile) return 'not_started'
    if (profile.kyc_status === 'approved') return 'approved'
    if (profile.kyc_status === 'rejected') return 'rejected'
    
    if (!profile.kyc) return 'not_started'
    
    if (profile.kyc.status === 'approved') return 'approved'
    if (profile.kyc.status === 'rejected') return 'rejected'
    
    return 'pending'
  }

  const getKYCStatusBadge = (status: string) => {
    const statusConfig = {
      'not_started': { 
        color: 'bg-dark-700 text-gray-300', 
        icon: ExclamationTriangleIcon, 
        text: 'Not Started' 
      },
      'pending': { 
        color: 'bg-yellow-100 text-yellow-800', 
        icon: ClockIcon, 
        text: 'Under Review' 
      },
      'approved': { 
        color: 'bg-green-100 text-green-800', 
        icon: CheckCircleIcon, 
        text: 'Approved' 
      },
      'rejected': { 
        color: 'bg-red-100 text-red-800', 
        icon: XCircleIcon, 
        text: 'Rejected' 
      }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.not_started
    const IconComponent = config.icon

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="w-4 h-4 mr-1" />
        {config.text}
      </span>
    )
  }

  const getESignStatusBadge = (status: string | undefined) => {
    if (getKycDisplayStatus() === 'not_started') return getKYCStatusBadge('not_started')
    
    const statusConfig = {
      'not_started': { color: 'bg-dark-700 text-gray-300', icon: ExclamationTriangleIcon, text: 'Not Started' },
      'initiated': { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, text: 'Pending Signature' },
      'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, text: 'Signed' },
      'failed': { color: 'bg-red-100 text-red-800', icon: XCircleIcon, text: 'Failed' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.not_started
    const IconComponent = config.icon

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="w-4 h-4 mr-1" />
        {config.text}
      </span>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Unable to load profile information</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-100">Profile</h1>
        <p className="mt-2 text-gray-300">
          Manage your account information and KYC details
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Profile Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <UserIcon className="w-6 h-6 text-primary-500 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-100">
                    Personal Information
                  </h2>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(!editMode)}
                >
                  <PencilIcon className="w-4 h-4 mr-2" />
                  {editMode ? 'Cancel' : 'Edit'}
                </Button>
              </div>

              {editMode ? (
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Full Name"
                      value={formData.full_name}
                      onChange={(e) => handleFieldChange('full_name', e.target.value)}
                      onBlur={(e) => handleFieldChange('full_name', e.target.value)}
                      error={formErrors.full_name}
                    />
                    <Input
                      label="Phone"
                      value={formData.phone}
                      onChange={(e) => handleFieldChange('phone', e.target.value)}
                      onBlur={(e) => handleFieldChange('phone', e.target.value)}
                      error={formErrors.phone}
                    />
                    <Input
                      label="Date of Birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    />
                    <Input
                      label="City"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                    />
                    <Input
                      label="State"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                    />
                    <Input
                      label="PIN Code"
                      value={formData.pincode}
                      onChange={(e) => handleFieldChange('pincode', e.target.value)}
                      onBlur={(e) => handleFieldChange('pincode', e.target.value)}
                      error={formErrors.pincode}
                      maxLength={6}
                    />
                  </div>
                  <Input
                    label="Address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                  />
                  <div className="flex space-x-3">
                    <Button type="submit">Save Changes</Button>
                    <Button variant="outline" onClick={() => setEditMode(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(profile.kyc?.pan_number || profile.pan_number) && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-400">PAN Number</label>
                      <p className="mt-1 text-sm text-gray-100 font-mono tracking-wide">
                        {profile.kyc?.pan_number || profile.pan_number}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Email</label>
                    <p className="mt-1 text-sm text-gray-100">{profile.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Username</label>
                    <p className="mt-1 text-sm text-gray-100">{profile.username}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Full Name</label>
                    <p className="mt-1 text-sm text-gray-100">{profile.full_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Phone</label>
                    <p className="mt-1 text-sm text-gray-100">{profile.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Date of Birth</label>
                    <p className="mt-1 text-sm text-gray-100">
                      {profile.date_of_birth ? formatDate(profile.date_of_birth) : 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Member Since</label>
                    <p className="mt-1 text-sm text-gray-100">{formatDate(profile.created_at)}</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-400">Address</label>
                    <p className="mt-1 text-sm text-gray-100">
                      {profile.address || 'Not provided'}
                      {profile.city && `, ${profile.city}`}
                      {profile.state && `, ${profile.state}`}
                      {profile.pincode && ` - ${profile.pincode}`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* KYC Information */}
          {profile.kyc && (
            <>
              {/* KYC Personal Details */}
              <Card>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <IdentificationIcon className="w-6 h-6 text-primary-500 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-100">
                      KYC Personal Details
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                                            <label className="block text-sm font-medium text-gray-400">Full Name</label>
                      <p className="mt-1 text-sm text-gray-100">{profile.kyc.full_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">Father's Name</label>
                      <p className="mt-1 text-sm text-gray-100">{profile.kyc.father_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">Date of Birth</label>
                      <p className="mt-1 text-sm text-gray-100">{formatDate(profile.kyc.date_of_birth)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">Gender</label>
                      <p className="mt-1 text-sm text-gray-100">{profile.kyc.gender}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-400">Address</label>
                      <p className="mt-1 text-sm text-gray-100">
                        {profile.kyc.address_line1}
                        {profile.kyc.address_line2 && `, ${profile.kyc.address_line2}`}
                        <br />
                        {profile.kyc.city}, {profile.kyc.state} - {profile.kyc.pincode}
                        <br />
                        {profile.kyc.country}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Bank Details */}
              {(profile.kyc.bank_name || profile.kyc.account_number || profile.kyc.ifsc_code) && (
                <Card>
                  <div className="p-6">
                    <div className="flex items-center mb-6">
                      <BanknotesIcon className="w-6 h-6 text-blue-600 mr-3" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        Bank Details
                      </h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Bank Name</label>
                        <p className="mt-1 text-sm text-gray-900">{profile.kyc.bank_name || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">IFSC Code</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">{profile.kyc.ifsc_code || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Account Number</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">
                          {profile.kyc.account_number ? '****' + profile.kyc.account_number.slice(-4) : 'Not provided'}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

            </>
          )}
        </div>

        {/* Right Column - Status & Actions */}
        <div className="space-y-6">
          {/* Account Status */}
          <Card>
            <div className="p-6">
              <div className="flex items-center mb-4">
                <ShieldCheckIcon className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-lg font-semibold text-gray-900">Account Status</h2>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Account</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    profile.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {profile.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Email Verified</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    profile.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {profile.is_verified ? 'Verified' : 'Pending'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">KYC Status</span>
                  {getKYCStatusBadge(getKycDisplayStatus())}
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">eSign Status</span>
                  {getESignStatusBadge(profile.kyc?.esign_status)}
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Stats */}
          {profile.referral_code && (
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Referral Code</h3>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Your Referral Code</p>
                  <p className="text-lg font-mono font-bold text-blue-600">{profile.referral_code}</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
